import './Task.scss';

import classNames from 'classnames';
import { ConfirmDeleteIcon } from '@ui/modals';
import { Draggable } from 'react-beautiful-dnd';
import { InitiativeScopeItem } from '../scopes';
import { MenuOutlined } from '@ant-design/icons';
import { PLACEHOLDERS_COPY } from '@sonar-software/sonar-ts-constants';
import { SonarText } from '@ui/text';
import { useAction } from '@hooks';
import { UserSelector } from '@ui/UserSelector';

import { Checkbox, Col, Row, Space } from 'antd';
import { coreActions, Task } from '@sonar-software/sonar-core';
import React, { useCallback, useState } from 'react';

export interface InitiativeTaskProps {
	index: number;
	task: Task;
}

export const InitiativeTask: React.FC<InitiativeTaskProps> = ({ index, task }) => {
	const [ checked, setChecked ] = useState(task.completed);
	const [ isEditing, setIsEditing ] = useState(false);
	const [ taskName, setTaskName ] = useState(task.name);

	const deleteTask = useAction(coreActions.requestDeleteCoreInitiativesTasksById);
	const deleteTaskLink = useAction(coreActions.requestDeleteCoreInitiativesTasksLink);
	const updateTask = useAction(coreActions.requestPatchCoreInitiativesTasksById);

	const { initiativeId, initiativeTaskId } = task;

	const startEditing = useCallback(() => {
		setIsEditing(true);
	}, []);

	const stopEditing = useCallback(() => {
		setIsEditing(false);
	}, []);

	const onChange = useCallback(
		(updatedName: string) => {
			const sanitizedName = updatedName?.trim();

			if (!sanitizedName || sanitizedName === taskName) {
				return;
			}

			updateTask({
				initiativeId,
				name: sanitizedName,
				taskId: initiativeTaskId
			});
			setTaskName(sanitizedName);
		},
		[ initiativeId, initiativeTaskId, taskName, updateTask ]
	);

	const onClickCheckbox = useCallback(() => {
		if (isEditing) {
			return;
		}

		const toggled = !checked;

		setChecked(toggled);
		updateTask({
			completed: toggled,
			initiativeId,
			taskId: initiativeTaskId
		});
	}, [ checked, initiativeId, initiativeTaskId, isEditing, updateTask ]);

	const onClickDelete = useCallback(() => {
		deleteTask({
			initiativeId,
			taskId: initiativeTaskId
		});
	}, [ deleteTask, initiativeId, initiativeTaskId ]);

	const unlinkScopeItem = (initiativeSonarObjectId: number, sonarObjectId: number) => {
		deleteTaskLink({
			initiativeId,
			initiativeSonarObjectId,
			initiativeTaskId,
			sonarObjectId
		});
	};

	const onOwnerChange = useCallback(
		(id?: number) => {
			updateTask({
				assignedTo: id ?? 0,
				initiativeId,
				taskId: initiativeTaskId
			});
		},
		[ initiativeId, initiativeTaskId, updateTask ]
	);

	const textClasses = classNames({ 'task-complete': checked });

	return (
		<Draggable
			draggableId={ String(initiativeTaskId) }
			index={ index }
			key={ initiativeTaskId }>
			{ (draggableProvided, dragSnapshot) =>
				<div
					className={ classNames('task', {
						dragging: dragSnapshot.isDragging
					}) }
					ref={ draggableProvided.innerRef }
					{ ...draggableProvided.draggableProps }
					{ ...draggableProvided.dragHandleProps }>
					<Row
						align='middle'
						gutter={ 8 }
						justify='space-between'
						wrap={ false }>
						<Col span={ isEditing ? 24 : undefined }>
							<Space
								align='start'
								onBlur={ stopEditing }>
								<MenuOutlined />
								<Checkbox
									checked={ checked }
									onClick={ onClickCheckbox } />
								<SonarText
									className={ textClasses }
									editable={ {
										editing: isEditing,
										icon: <span />,
										maxLength: 255,
										onCancel: stopEditing,
										onChange: onChange,
										onEnd: stopEditing
									} }
									onClick={ startEditing }>
									{ taskName }
								</SonarText>
							</Space>
						</Col>
						<Col
							className='task-action-area'
							span={ isEditing ? 0 : undefined }>
							<Space align='center'>
								<UserSelector
									allowClear
									avatar
									onSingleChange={ onOwnerChange }
									placeholder={ PLACEHOLDERS_COPY.ADD_OWNER }
									value={ task.assignedTo ? task.assignedTo : undefined } />
								<div className='task-icon-area'>
									<ConfirmDeleteIcon onConfirm={ onClickDelete } />
								</div>
							</Space>
						</Col>
					</Row>
					<div className='task-scope'>
						{ task.sonarObjects.map(({ itemId, sonarObjectId }) =>
							<InitiativeScopeItem
								initiativeId={ initiativeId }
								itemId={ itemId }
								key={ sonarObjectId }
								removeAction={ unlinkScopeItem }
								sonarObjectId={ sonarObjectId } />) }
					</div>
				</div> }
		</Draggable>
	);
};
